import { AGENT_PROFILE_PRESETS } from "~/lib/onboarding/agent-profile-presets";
import type { IndustryVertical } from "~/lib/utils/industry-profile";
import { resolveIndustryProfile } from "~/lib/utils/industry-profile";
import { suggestedOrganizationNameFromWebsiteUrl } from "~/lib/onboarding/org-name-from-website";
import {
  normalizeHireCompanyDisplayName,
  resolveHireBusinessDisplayName,
} from "~/lib/marketplace/hire-company-name";
import { buildOnboardingGoalsFromWebsite } from "~/lib/marketplace/hire-builder-onboarding";
import type { HireTeamHandoffCtaCluster } from "~/lib/marketplace/hire-team-handoff-ctas";
import {
  buildDefaultTeamHandoffCtaClustersForHire,
} from "~/lib/marketplace/hire-team-handoff-ctas";
import type { HireLocationDetail } from "~/lib/marketplace/hire-recommendations-pricing";
import { locationListToDetails } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  agentsForCategory,
  MOONLIGHT_CATEGORIES,
  type MoonlightAgentListItem,
  type MoonlightCategoryId,
} from "~/lib/marketplace/moonlight-home";
import { marketplaceAgentSearchHaystack } from "~/lib/marketplace/agent-vertical";
import {
  detectWebsiteSchedulingSignals,
  type WebsiteSchedulingSignals,
} from "~/lib/marketplace/website-scheduling-signals";

export type RecommendedAgentProfile = {
  agentName: string;
  jobTitle: string;
  profileImageUrl: string;
  persona: string;
};

export const DEFAULT_HIRE_INTEGRATIONS = [
  "Google Sheets",
  "WhatConverts",
  "TapClick",
] as const;

export type WebsiteLeadTeamHints = {
  jobTitle?: string;
  phone?: string;
  imageUrl?: string;
};

export type WebsiteSetupRecommendations = {
  agentProfile: RecommendedAgentProfile;
  skills: string[];
  integrations: string[];
  emrIntegration: string | null;
  appointmentsScheduling: string[];
  locations: string;
  locationList: string[];
  locationDetails?: HireLocationDetail[];
  teamMembers: string;
  leadTeamHints?: WebsiteLeadTeamHints;
  teamHandoffCtaClusters?: HireTeamHandoffCtaCluster[];
  /** @deprecated Legacy field — ignored in UI. */
  communication?: string[];
  /** @deprecated Legacy field — use integrations. */
  tools?: string[];
};

export type WebsiteAgentSuggestion = {
  websiteUrl: string;
  businessName: string;
  categoryId: MoonlightCategoryId;
  categoryLabel: string;
  vertical: IndustryVertical;
  summary: string;
  /** Goal chips shown after website analysis in hire/custom onboarding. */
  onboardingGoalSuggestions?: string[];
  /** Brand colors scraped from the website CSS (when available). */
  brandColors?: {
    headerColor: string;
    widgetButtonColor: string;
    launcherBackgroundColor: string;
    textColor: string;
  };
  recommendations: WebsiteSetupRecommendations;
  /** Inferred scheduling / CTA / platform signals from crawl (labeled inferred). */
  schedulingSignals?: WebsiteSchedulingSignals;
};

export function normalizeMarketplaceWebsiteUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    if (!url.hostname.includes(".")) return null;
    return url.href.replace(/\/$/, "");
  } catch {
    return null;
  }
}

const MARKETPLACE_FETCH_HEADERS: HeadersInit = {
  "User-Agent":
    "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
  Accept:
    "text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,*/*;q=0.8",
  "Accept-Language": "en-US,en;q=0.9",
  "Cache-Control": "no-cache",
  "Upgrade-Insecure-Requests": "1",
};

export function marketplaceWebsiteUrlVariants(normalized: string): string[] {
  const candidates = new Set<string>([normalized]);
  try {
    const parsed = new URL(normalized);
    const bareHost = parsed.hostname.replace(/^www\./i, "");
    const wwwHost = parsed.hostname.startsWith("www.") ? parsed.hostname : `www.${parsed.hostname}`;
    const path = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
    candidates.add(`${parsed.protocol}//${bareHost}${path}`);
    candidates.add(`${parsed.protocol}//${wwwHost}${path}`);
    if (parsed.protocol === "https:") {
      candidates.add(`http://${bareHost}${path}`);
      candidates.add(`http://${wwwHost}${path}`);
    }
  } catch {
    /* ignore */
  }
  return [...candidates];
}

/** Fetch homepage text for marketplace suggestions; tries browser-like headers and URL variants. */
export async function fetchMarketplaceWebsiteText(url: string): Promise<string | null> {
  for (const tryUrl of marketplaceWebsiteUrlVariants(url)) {
    try {
      const response = await fetch(tryUrl, {
        headers: MARKETPLACE_FETCH_HEADERS,
        signal: AbortSignal.timeout(15_000),
        redirect: "follow",
      });
      if (!response.ok) continue;
      const html = await response.text();
      if (html.length < 200) continue;

      const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
      const metaMatch = html.match(
        /<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["']/i,
      );
      const ogMatch = html.match(
        /<meta[^>]+property=["']og:site_name["'][^>]+content=["']([^"']+)["']/i,
      );

      // Deeper crawl: keep booking-widget / portal script URLs and iframe srcs before stripping scripts
      const embedHints: string[] = [];
      for (const m of html.matchAll(
        /<(?:script|iframe)[^>]+src=["']([^"']+)["'][^>]*>/gi,
      )) {
        const src = m[1]?.toLowerCase() ?? "";
        if (
          /calendly|jane\.app|nexhealth|juvonno|acuity|mindbody|squareup|cliniko|hubspot|setmore|booker|zocdoc|patientportal|mychart/.test(
            src,
          )
        ) {
          embedHints.push(src);
        }
      }
      for (const m of html.matchAll(/href=["']([^"']+)["']/gi)) {
        const href = m[1]?.toLowerCase() ?? "";
        if (
          /book|schedule|appointment|patient.?portal|client.?portal/.test(href) &&
          embedHints.length < 20
        ) {
          embedHints.push(href);
        }
      }

      const bodyText = html
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[\s\S]*?<\/style>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 4000);

      const combined = [
        titleMatch?.[1]?.trim(),
        ogMatch?.[1]?.trim(),
        metaMatch?.[1]?.trim(),
        embedHints.length ? `Embeds: ${embedHints.join(" ")}` : null,
        bodyText,
      ]
        .filter(Boolean)
        .join("\n");

      if (combined.length > 50) return combined;
    } catch {
      continue;
    }
  }
  return null;
}

export function moonlightCategoryFromVertical(vertical: IndustryVertical): MoonlightCategoryId {
  const map: Partial<Record<IndustryVertical, MoonlightCategoryId>> = {
    healthcare: "healthcare",
    legal: "legal",
    home_services: "home_services",
    real_estate: "real_estate",
    fitness: "fitness",
    hospitality: "restaurant",
    financial: "financial",
    retail: "ecommerce",
    education: "education",
    automotive: "automotive",
  };
  return map[vertical] ?? "all";
}

function contentIncludes(content: string, terms: string[]): boolean {
  const lower = content.toLowerCase();
  return terms.some((term) => {
    const t = term.toLowerCase();
    // Whole-word match for short tokens that are substrings of other words
    // (e.g. "clinic" must not match "clinical" on education sites).
    if (t === "clinic" || t === "patient" || t === "care") {
      return new RegExp(`\\b${t}\\b`, "i").test(lower);
    }
    return lower.includes(t);
  });
}

function presetUrlMatching(labelFragment: string): string {
  const preset = AGENT_PROFILE_PRESETS.find((item) =>
    item.label.toLowerCase().includes(labelFragment.toLowerCase()),
  );
  return preset?.url ?? AGENT_PROFILE_PRESETS[2]!.url;
}

/** Agent name, title, and avatar derived from website vertical + page content. */
export function buildRecommendedAgentProfile(
  vertical: IndustryVertical,
  contentText: string,
): RecommendedAgentProfile {
  const isDental = contentIncludes(contentText, [
    "dental",
    "dentist",
    "orthodont",
    "oral surgery",
    "periodont",
  ]);
  const isClinicEmr = contentIncludes(contentText, [
    "physical therapy",
    "physiotherapy",
    "chiropractic",
    "chiropractor",
    "rehab",
    "clinic",
  ]);
  const isLegal =
    vertical === "legal" ||
    contentIncludes(contentText, ["law firm", "attorney", "lawyer", " legal ", "attorneys and advocates"]);
  const isHvac = contentIncludes(contentText, [
    "hvac",
    "heating",
    "air conditioning",
    "air systems",
    "furnace",
    "cooling",
  ]);

  if (isDental && vertical === "healthcare") {
    return {
      agentName: "Denise",
      jobTitle: "New Patient Coordinator",
      profileImageUrl: presetUrlMatching("Denise"),
    };
  }

  if (isClinicEmr && vertical === "healthcare") {
    return {
      agentName: "Annie",
      jobTitle: "Patient Care Coordinator",
      profileImageUrl: presetUrlMatching("Annie"),
    };
  }

  if (vertical === "healthcare") {
    return {
      agentName: "Annie",
      jobTitle: "Patient Intake Coordinator",
      profileImageUrl: presetUrlMatching("Annie"),
    };
  }

  if (isLegal) {
    return {
      agentName: "Victoria",
      jobTitle: "Client Intake Specialist",
      profileImageUrl: presetUrlMatching("Victoria"),
    };
  }

  if (vertical === "home_services" || isHvac) {
    return {
      agentName: "Sam",
      jobTitle: "Online Support",
      profileImageUrl: presetUrlMatching("Sam"),
    };
  }

  if (vertical === "real_estate") {
    return {
      agentName: "Andrew",
      jobTitle: "Client Concierge",
      profileImageUrl: presetUrlMatching("Andrew"),
    };
  }

  if (vertical === "hospitality") {
    return {
      agentName: "Mia",
      jobTitle: "Front Desk Receptionist",
      profileImageUrl: presetUrlMatching("Mia"),
    };
  }

  if (vertical === "financial") {
    return {
      agentName: "Christie",
      jobTitle: "Client Services Representative",
      profileImageUrl: presetUrlMatching("Christie"),
    };
  }

  if (vertical === "fitness") {
    return {
      agentName: "Rebecca",
      jobTitle: "Membership Coordinator",
      profileImageUrl: presetUrlMatching("Rebecca"),
    };
  }

  if (vertical === "automotive") {
    return {
      agentName: "Ben",
      jobTitle: "Service Advisor",
      profileImageUrl: presetUrlMatching("Ben"),
    };
  }

  if (vertical === "education") {
    return {
      agentName: "Evelyn",
      jobTitle: "Enrollment Coordinator",
      profileImageUrl: presetUrlMatching("Evelyn"),
    };
  }

  if (vertical === "nonprofit") {
    const isChurch = contentIncludes(contentText, [
      "church",
      "parish",
      "catholic",
      "ministry",
      "worship",
      "diocese",
    ]);
    return {
      // Grace — never day-of-week names like "Sunday" for churches.
      agentName: isChurch ? "Grace" : "Mia",
      jobTitle: isChurch ? "Digital Welcome Coordinator" : "Community Outreach Assistant",
      profileImageUrl: presetUrlMatching(isChurch ? "Mary" : "Mia"),
    };
  }

  if (vertical === "retail") {
    return {
      agentName: "Lisa",
      jobTitle: "Customer Support Specialist",
      profileImageUrl: presetUrlMatching("Lisa"),
    };
  }

  if (vertical === "consulting") {
    const isEngineeringFirm = contentIncludes(contentText, [
      "engineering",
      "mep",
      "mechanical",
      "commissioning",
      "life safety",
      "built environment",
      "fire protection",
    ]);
    if (isEngineeringFirm) {
      return {
        agentName: "Ben",
        jobTitle: "Sales Engineer",
        profileImageUrl: presetUrlMatching("Ben"),
      };
    }
    return {
      agentName: "Samantha",
      jobTitle: "Client Success Coordinator",
      profileImageUrl: presetUrlMatching("Samantha"),
    };
  }

  if (vertical === "manufacturing") {
    return {
      agentName: "Ben",
      jobTitle: "Sales Engineer",
      profileImageUrl: presetUrlMatching("Ben"),
    };
  }

  return {
    agentName: "Samantha",
    jobTitle: "Online Support",
    profileImageUrl: presetUrlMatching("Samantha"),
  };
}

export function buildRecommendedAgentPersona(
  agentName: string,
  jobTitle: string,
  businessName: string,
): string {
  return `I'm ${agentName}, your friendly ${jobTitle} at ${businessName}. I'm dedicated to helping visitors get quick answers, connecting them with the right team member when needed, and making every interaction feel personal and professional.`;
}

function buildRecommendedAgentProfileWithPersona(
  vertical: IndustryVertical,
  contentText: string,
  businessName: string,
): RecommendedAgentProfile {
  const profile = buildRecommendedAgentProfile(vertical, contentText);
  return {
    ...profile,
    persona: buildRecommendedAgentPersona(
      profile.agentName,
      profile.jobTitle,
      businessName,
    ),
  };
}

function buildEmrIntegrationLabel(
  vertical: IndustryVertical,
  contentText: string,
): string | null {
  // Never recommend healthcare EMR/PMS outside healthcare verticals.
  if (vertical !== "healthcare") return null;

  const isDental = contentIncludes(contentText, [
    "dental",
    "dentist",
    "orthodont",
    "oral surgery",
    "periodont",
  ]);
  const isClinicEmr = contentIncludes(contentText, [
    "physical therapy",
    "physiotherapy",
    "chiropractic",
    "chiropractor",
    "rehab",
    "clinic",
  ]);

  if (isDental) return "NexHealth";
  if (isClinicEmr) return "Juvonno";
  return "NexHealth";
}

/** Recommended agent setup sections derived from website vertical + page content. */
export function buildWebsiteSetupRecommendations(
  vertical: IndustryVertical,
  contentText: string,
  locationCount = 1,
  locationList: string[] = [],
  locationDetails: HireLocationDetail[] = [],
  businessName = "your business",
): WebsiteSetupRecommendations {
  const isDental = contentIncludes(contentText, [
    "dental",
    "dentist",
    "orthodont",
    "oral surgery",
    "periodont",
  ]);
  const isClinicEmr = contentIncludes(contentText, [
    "physical therapy",
    "physiotherapy",
    "chiropractic",
    "chiropractor",
    "rehab",
    "clinic",
  ]);
  const isLegal =
    vertical === "legal" ||
    contentIncludes(contentText, ["law firm", "attorney", "lawyer", " legal ", "attorneys and advocates"]);

  const skills = new Set<string>(["Live Chat", "Team Handoff"]);

  if (
    vertical === "healthcare" ||
    vertical === "legal" ||
    vertical === "home_services" ||
    vertical === "automotive" ||
    vertical === "financial" ||
    vertical === "real_estate"
  ) {
    skills.add("Phone Calls");
  }
  if (
    vertical === "education" ||
    vertical === "fitness" ||
    contentIncludes(contentText, ["video call", "virtual tour", "telehealth"])
  ) {
    skills.add("Video Calls");
  }
  if (
    vertical === "healthcare" ||
    vertical === "home_services" ||
    vertical === "legal" ||
    vertical === "fitness" ||
    vertical === "automotive" ||
    vertical === "real_estate" ||
    contentIncludes(contentText, ["appointment", "schedule", "book online", "booking"])
  ) {
    skills.add("Appointments & Scheduling");
  }

  let appointmentsScheduling: string[] = [];

  if (vertical === "education") {
    appointmentsScheduling = ["Calendly", "Google Calendar", "Zoom"];
  } else if (isDental && vertical === "healthcare") {
    appointmentsScheduling = ["NexHealth"];
  } else if (isClinicEmr && vertical === "healthcare") {
    appointmentsScheduling = ["Juvonno"];
  } else if (isLegal) {
    appointmentsScheduling = ["Calendly"];
  } else if (vertical === "home_services" || vertical === "automotive") {
    appointmentsScheduling = ["Google Calendar"];
  } else if (vertical === "healthcare") {
    appointmentsScheduling = ["NexHealth"];
  } else if (vertical === "hospitality" || vertical === "fitness") {
    appointmentsScheduling = ["Calendly"];
  } else {
    appointmentsScheduling = ["Google Calendar"];
  }

  const emrIntegration = buildEmrIntegrationLabel(vertical, contentText);

  const normalizedLocationCount = Math.max(1, locationList.length || Math.floor(locationCount));

  const locations =
    locationList.length > 0
      ? `${locationList.length} location${locationList.length === 1 ? "" : "s"} (detected from your website)`
      : normalizedLocationCount <= 1
        ? "1 location (main office)"
        : `${normalizedLocationCount} locations (detected from your website)`;

  const teamMembers =
    normalizedLocationCount <= 1
      ? "Lead Team Member + Account Owner"
      : `${normalizedLocationCount} Lead Team Members (one per location) + Account Owner`;

  const teamHandoffCtaClusters = buildDefaultTeamHandoffCtaClustersForHire(
    vertical,
    contentText,
    businessName,
  );

  return {
    agentProfile: buildRecommendedAgentProfileWithPersona(
      vertical,
      contentText,
      businessName,
    ),
    skills: [...skills],
    integrations: [...DEFAULT_HIRE_INTEGRATIONS],
    emrIntegration,
    appointmentsScheduling,
    locations,
    locationList,
    locationDetails:
      locationDetails.length > 0
        ? locationDetails
        : locationListToDetails(locationList),
    teamMembers,
    teamHandoffCtaClusters,
  };
}

export function buildWebsiteSuggestionFromContent(
  websiteUrl: string,
  contentText: string,
  options?: {
    locationCount?: number;
    locationList?: string[];
    locationDetails?: HireLocationDetail[];
    businessName?: string;
  },
): WebsiteAgentSuggestion {
  const profile = resolveIndustryProfile(null, contentText);
  const categoryId = moonlightCategoryFromVertical(profile.vertical);
  const categoryLabel =
    MOONLIGHT_CATEGORIES.find((c) => c.id === categoryId)?.label ?? "All industries";
  const rawBusinessName =
    options?.businessName?.trim() ||
    extractBusinessNameFromContent(websiteUrl, contentText) ||
    suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
  const businessName = resolveHireBusinessDisplayName(rawBusinessName, websiteUrl) || rawBusinessName;
  const onboardingGoalSuggestions = buildOnboardingGoalsFromWebsite(
    profile.vertical,
    contentText,
    businessName,
  );

  const summary = `We analyzed ${businessName}'s website and built a recommended AI Employee setup. Review the findings with Trish, then continue to onboard.`;

  const locationCount = options?.locationCount ?? 1;
  const locationList = options?.locationList ?? [];
  const locationDetails = options?.locationDetails ?? [];
  const schedulingSignals = detectWebsiteSchedulingSignals(contentText);

  return {
    websiteUrl,
    businessName,
    categoryId,
    categoryLabel,
    vertical: profile.vertical,
    summary,
    onboardingGoalSuggestions,
    schedulingSignals,
    recommendations: buildWebsiteSetupRecommendations(
      profile.vertical,
      contentText,
      locationCount,
      locationList,
      locationDetails,
      businessName,
    ),
  };
}

function extractBusinessNameFromContent(websiteUrl: string, contentText: string): string | null {
  const firstLine = contentText.split("\n").map((line) => line.trim()).find(Boolean);
  if (firstLine) {
    const segment = firstLine.split(/\||\u2013|\u2014|-/)[0]?.trim();
    if (segment && segment.length >= 2 && segment.length <= 80) {
      return resolveHireBusinessDisplayName(segment, websiteUrl) || segment;
    }
  }

  return suggestedOrganizationNameFromWebsiteUrl(websiteUrl);
}

export function rankSuggestedAgents(
  agents: MoonlightAgentListItem[],
  categoryId: MoonlightCategoryId,
  contentText: string,
  limit = 4,
): MoonlightAgentListItem[] {
  const inCategory = agentsForCategory(agents, categoryId);
  const pool = inCategory.length >= 2 ? inCategory : agents;
  const tokens = contentText
    .toLowerCase()
    .split(/\W+/)
    .filter((token) => token.length > 3);

  const scoreAgent = (agent: MoonlightAgentListItem): number => {
    const haystack = marketplaceAgentSearchHaystack(agent).join(" ").toLowerCase();
    let score = 0;
    if (agent.listingType === "OFFICIAL") score += 4;
    if (agent.featured) score += 2;
    if (inCategory.some((item) => item.id === agent.id)) score += 6;
    for (const token of tokens) {
      if (haystack.includes(token)) score += 1;
    }
    return score;
  };

  return [...pool]
    .sort((a, b) => scoreAgent(b) - scoreAgent(a) || a.title.localeCompare(b.title))
    .slice(0, limit);
}
